/**
 * Babcock National HOA Member Central calendar — seeded snapshot.
 *
 * The live Event Calendar is login-gated (Member Central). CI and the
 * Worker never attempt that login. What's On serves this in-repo JSON
 * as `kind: 'hoa_national'` and merges it with the open town RSS feeds.
 *
 * Refresh path (manual, from a signed-in browser — not CI):
 *   1. Sign in at HOA_NATIONAL_MEMBER_CENTRAL_URL
 *   2. Open HOA_NATIONAL_CALENDAR_URL
 *   3. Replace data/hoa-national-events.json with the same row shape
 *      (date, title, start_end_time, location, description, links, categories)
 *   4. Open a PR. start_end_time is parsed to 24h startTime/endTime here.
 *
 * Snapshot coverage: 2026-09-14 → 2026-11-02 (100 events).
 */

import hoaNationalSeedJson from '../data/hoa-national-events.json';
import { parseFeedTime, type RanchEvent } from './ranch-feed';

export const HOA_NATIONAL_CALENDAR_URL =
  'https://www.babcocknationalhoa.com/lifestyle-and-events/event-calendar-47.html';
export const HOA_NATIONAL_MEMBER_CENTRAL_URL =
  'https://www.babcocknationalhoa.com/hoa-member-s-corner/member-central-38.html';
export const HOA_NATIONAL_DINING_RESERVATION_URL =
  'https://www.babcocknationalhoa.com/dining/make-a-reservation-100.html';
export const HOA_NATIONAL_ENCLAVE_MENUS_URL =
  'https://www.babcocknationalhoa.com/_filelib/FileCabinet/Food_and_Beverage/Enclave_Menus-Updated_JULY_2026.pdf';
export const HOA_NATIONAL_PHONE_DISPLAY = '(941) 347-2200';
export const HOA_NATIONAL_PHONE = '9413472200';
export const HOA_NATIONAL_ADDRESS = '43091 Greenway Blvd, Punta Gorda, FL 33982';

export interface HoaNationalSeedEvent {
  date: string;
  title: string;
  start_end_time: string;
  location: string;
  description: string;
  links: string[];
  categories: string;
}

const hoaNationalSeed = hoaNationalSeedJson as HoaNationalSeedEvent[];

/**
 * HOA times look like `1:00PM-4:00PM` or `1:00PM` — no space before AM/PM,
 * unlike the town RSS `05:00 PM`. Normalize, then reuse parseFeedTime.
 */
export function parseHoaTime(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const compact = raw.trim().replace(/\s+/g, '');
  const spaced = compact.replace(/([AP]M)$/i, ' $1');
  return parseFeedTime(spaced);
}

export function parseHoaStartEndTime(
  raw: string | null | undefined,
): { startTime: string | null; endTime: string | null } {
  if (!raw || !raw.trim()) return { startTime: null, endTime: null };
  const parts = raw.trim().split(/\s*-\s*/);
  return {
    startTime: parseHoaTime(parts[0]),
    endTime: parts.length > 1 ? parseHoaTime(parts[1]) : null,
  };
}

function slugTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

/** Always prefixed `hoa:` so numeric EventIDs cannot collide with town EIDs. */
export function hoaNationalEventId(seed: HoaNationalSeedEvent, index: number): string {
  const eventId = seed.links
    ?.map((href) => href.match(/[?&]EventID=(\d+)/i)?.[1])
    .find(Boolean);
  if (eventId) return `hoa:${seed.date}:${eventId}`;
  const slug = slugTitle(seed.title) || 'event';
  return `hoa:${seed.date}:${slug}:${index}`;
}

export function hoaSeedToRanchEvent(seed: HoaNationalSeedEvent, index: number): RanchEvent {
  const { startTime, endTime } = parseHoaStartEndTime(seed.start_end_time);
  const location = seed.location?.trim() || null;
  const description = seed.description?.trim() || null;
  const url = seed.links?.[0] || HOA_NATIONAL_CALENDAR_URL;
  return {
    id: hoaNationalEventId(seed, index),
    title: seed.title.trim(),
    date: seed.date,
    startTime,
    endTime,
    location,
    description,
    url,
    kind: 'hoa_national',
  };
}

/** In-repo snapshot. Never fetches; never logs into Member Central. */
export function loadHoaNationalEvents(seeds: HoaNationalSeedEvent[] = hoaNationalSeed): RanchEvent[] {
  return seeds
    .filter((row) => row.title?.trim() && /^\d{4}-\d{2}-\d{2}$/.test(row.date))
    .map(hoaSeedToRanchEvent);
}
